import { getStableDeviceId } from './deviceId';
import { getMeetingDisplayName } from './meetingDisplayName';

const TAB_AUTH_BINDING_KEY = 'meetingTabSessionId';

const safeStorage = (storage, method, key, value) => {
  try {
    return value === undefined
      ? storage[method](key)
      : storage[method](key, value);
  } catch {
    return null;
  }
};

export const readStoredAuth = () => {
  for (const storage of [sessionStorage, localStorage]) {
    try {
      const value = JSON.parse(storage.getItem('auth_v1') || 'null');
      if (value?.token && value?.user) {
        return value;
      }
    } catch {}
  }
  return null;
};

export const getStoredAccessToken = () =>
  // auth_v1 is written atomically with the matching user/session. Legacy
  // top-level keys can survive older releases and must never override it.
  readStoredAuth()?.token ||
  sessionStorage.getItem('accessToken') ||
  sessionStorage.getItem('token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token') ||
  '';

export const getOrCreateMeetingDeviceId = () => getStableDeviceId();

export const getStoredMeetingSessionId = () =>
  readStoredAuth()?.sessionId ||
  sessionStorage.getItem('sessionId') ||
  localStorage.getItem('sessionId') ||
  '';

export const getStoredMeetingUserId = () => {
  const auth = readStoredAuth();
  return (
    auth?.user?._id ||
    auth?.user?.id ||
    sessionStorage.getItem('userId') ||
    localStorage.getItem('userId') ||
    ''
  );
};

/**
 * Older dashboard logins persist auth only in localStorage. Hydrate the exact
 * identity into this tab before any room lock, socket join or MediaSoup REST
 * request is created.
 */
export const hydrateMeetingIdentity = () => {
  const auth = readStoredAuth();
  const token = getStoredAccessToken();
  const sessionId = getStoredMeetingSessionId();
  const userId = getStoredMeetingUserId();
  if (token) {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('accessToken', token);
  }
  if (sessionId) sessionStorage.setItem('sessionId', String(sessionId));
  if (userId) sessionStorage.setItem('userId', String(userId));
  if (auth) {
    sessionStorage.setItem('auth_v1', JSON.stringify(auth));
    if (sessionId) {
      sessionStorage.setItem(TAB_AUTH_BINDING_KEY, String(sessionId));
    }
  }
  return {
    token,
    sessionId: String(sessionId || ''),
    userId: String(userId || ''),
    user: auth?.user || null,
    valid: Boolean(token && sessionId && userId),
  };
};

export const ensureMeetingSessionId = () => {
  const existing = String(getStoredMeetingSessionId() || '').trim();
  if (existing) {
    sessionStorage.setItem('sessionId', existing);
    const auth = readStoredAuth();
    if (auth?.token && auth?.user) {
      sessionStorage.setItem(
        'auth_v1',
        JSON.stringify({ ...auth, sessionId: existing })
      );
      sessionStorage.setItem(TAB_AUTH_BINDING_KEY, existing);
    }
    return existing;
  }

  const generated =
    globalThis.crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random()
      .toString(36)
      .slice(2)}`;
  sessionStorage.setItem('sessionId', generated);

  // Keep token, user and session as one atomic tab-scoped identity. The
  // backend's authenticated join flow binds this newly-created session before
  // MeetingRoom or MediaSoup uses it.
  const auth = readStoredAuth();
  if (auth?.token && auth?.user) {
    sessionStorage.setItem(
      'auth_v1',
      JSON.stringify({ ...auth, sessionId: generated })
    );
    sessionStorage.setItem(TAB_AUTH_BINDING_KEY, generated);
  }
  return generated;
};

export const getStoredMeetingAccess = () => {
  try {
    return JSON.parse(sessionStorage.getItem('meetingAccess') || '{}');
  } catch {
    return {};
  }
};

export const persistRoomAccess = ({ access = {}, status, meetingType }) => {
  const values = {
    meetingAccess: JSON.stringify(access || {}),
    isHost: String(access.isHost === true),
    isCoHost: String(access.isCoHost === true),
    isCoStaffHost: String(access.isCoStaffHost === true),
    canAdmit: String(access.canAdmit === true),
    canReject: String(access.canReject === true),
    canManageMedia: String(access.canManageMedia === true),
    canRecord: String(access.canRecord === true),
    canUseBlackboard: String(access.canUseBlackboard === true),
    canEndMeeting: String(access.canEndMeeting === true),
  };

  Object.entries(values).forEach(([key, value]) =>
    safeStorage(sessionStorage, 'setItem', key, value)
  );
  if (status) sessionStorage.setItem('statusget', status);
  if (meetingType) sessionStorage.setItem('meetingType', meetingType);
};

export const persistMeetingAuth = ({
  token,
  user,
  sessionId,
  status = 'waiting',
  meetingType = 'meeting',
  access = {},
}) => {
  const userId = user?._id || user?.id;
  if (!token || !userId || !sessionId) {
    throw new Error('Incomplete meeting authentication response');
  }

  const authPayload = { user, token, sessionId, persistedAt: Date.now() };
  const serialized = JSON.stringify(authPayload);

  // A signed invite creates a room-specific participant session. Keep it in
  // this tab only; writing it to localStorage would replace the identity used
  // by another active meeting tab.
  sessionStorage.setItem('auth_v1', serialized);
  sessionStorage.setItem('accessToken', token);
  sessionStorage.setItem('token', token);
  sessionStorage.setItem('sessionId', sessionId);
  sessionStorage.setItem('userId', String(userId));
  sessionStorage.setItem('userRole', String(user.role || ''));
  sessionStorage.setItem(TAB_AUTH_BINDING_KEY, String(sessionId));

  getStableDeviceId();
  sessionStorage.setItem('userName', getMeetingDisplayName(user, 'User'));
  persistRoomAccess({ access, status, meetingType });
  sessionStorage.removeItem('meetingEnd');
  window.dispatchEvent(new Event('auth-changed'));
  return authPayload;
};

export const clearMeetingRuntimeStorage = ({ keepAuth = true } = {}) => {
  [
    'meetingType', 'statusget', 'isHost', 'isCoHost', 'isCoStaffHost',
    'canAdmit', 'canReject', 'canManageMedia', 'canRecord',
    'canUseBlackboard', 'canEndMeeting', 'meetingAccess', 'rejoinStatus',
    'rejoin-room', 'lastRoomId', 'meetingEnd',
  ].forEach((key) => sessionStorage.removeItem(key));

  if (!keepAuth) {
    for (const storage of [localStorage, sessionStorage]) {
      ['auth_v1', 'accessToken', 'token', 'sessionId', 'userId', 'userRole']
        .forEach((key) => storage.removeItem(key));
    }
    sessionStorage.removeItem(TAB_AUTH_BINDING_KEY);
  }
};
